// components/ThemeDropdown.jsx
import React, { Fragment, useRef } from "react";
import { Menu, Transition, Portal } from "@headlessui/react";
import { Moon, Sun, Laptop, Check } from "lucide-react";
import { useTheme } from "hooks/useTheme";
import { useAnchorRect } from "hooks/useAnchorRect";

const items = [
  { key: "light", label: "Light", Icon: Sun },
  { key: "dark", label: "Dark", Icon: Moon },
  { key: "system", label: "System", Icon: Laptop },
];

/**
 * ThemeDropdown — light / dark / system.
 *
 * SOUND-616/617: the menu is PORTALED to document.body. As a child of the top
 * bar it was trapped in AccountLayout's `sticky z-20` stacking context, so
 * PageLayout's sticky `z-30 backdrop-blur` header painted over it — the same bug
 * reported against the notification bell, on every page with a sticky header.
 * A portal escapes all stacking contexts; `useAnchorRect` restores the
 * right-alignment that `absolute right-0` used to provide.
 *
 * Headless UI's Menu keeps managing open state, focus, and outside-clicks
 * itself, so unlike the hand-rolled bell there is no DOM-contains check here to
 * get wrong.
 */
export default function ThemeDropdown() {
  const { mode, setTheme, isDark } = useTheme();
  const btnRef = useRef(null);

  return (
    <Menu as="div" className="relative inline-block text-left">
      {({ open }) => (
        <>
          <Menu.Button ref={btnRef} className="btn btn-ghost h-9 w-9 p-0" aria-label="Theme" title="Theme">
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Menu.Button>
          <ThemeMenuItems open={open} btnRef={btnRef} mode={mode} setTheme={setTheme} />
        </>
      )}
    </Menu>
  );
}

// Split out so the anchor hook can react to Menu's own `open` render-prop.
function ThemeMenuItems({ open, btnRef, mode, setTheme }) {
  const anchor = useAnchorRect(open, btnRef);

  return (
    <Portal>
      <Transition
        as={Fragment}
        show={open}
        enter="transition ease-out duration-100"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-75"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <Menu.Items
          static
          style={anchor ? { top: anchor.top, right: anchor.right } : { visibility: "hidden" }}
          className="fixed z-[60] w-40 origin-top-right rounded-md border border-border bg-card text-card-foreground shadow-lg focus:outline-none"
        >
          <div className="py-1">
            {items.map(({ key, label, Icon }) => (
              <Menu.Item key={key}>
                {({ active }) => (
                  <button
                    onClick={() => setTheme(key)}
                    className={`w-full px-3 py-2 text-left text-sm inline-flex items-center gap-2 ${
                      active ? "bg-muted/70" : ""
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="flex-1">{label}</span>
                    {mode === key && <Check className="h-4 w-4" />}
                  </button>
                )}
              </Menu.Item>
            ))}
          </div>
        </Menu.Items>
      </Transition>
    </Portal>
  );
}
